import * as React from 'react';

import {
  Drawer as ShadcnDrawer,
  DrawerClose as ShadcnDrawerClose,
  DrawerContent as ShadcnDrawerContent,
  DrawerDescription as ShadcnDrawerDescription,
  DrawerFooter as ShadcnDrawerFooter,
  DrawerHeader as ShadcnDrawerHeader,
  DrawerOverlay as ShadcnDrawerOverlay,
  DrawerPortal as ShadcnDrawerPortal,
  DrawerTitle as ShadcnDrawerTitle,
  DrawerTrigger as ShadcnDrawerTrigger,
} from '@/hen-ui/shadcn/drawer';
import {
  drawerContentVariants,
  type DrawerContentVariantProps,
} from './drawer.variants';
import { cn } from '@/hen-ui/utils';

export type DrawerProps = React.ComponentPropsWithoutRef<typeof ShadcnDrawer>;

export interface DrawerTriggerProps
  extends React.ComponentPropsWithoutRef<typeof ShadcnDrawerTrigger> {}

export interface DrawerPortalProps
  extends React.ComponentPropsWithoutRef<typeof ShadcnDrawerPortal> {}

export interface DrawerCloseProps
  extends React.ComponentPropsWithoutRef<typeof ShadcnDrawerClose> {}

export interface DrawerOverlayProps
  extends React.ComponentPropsWithoutRef<typeof ShadcnDrawerOverlay> {}

export interface DrawerContentProps
  extends React.ComponentPropsWithoutRef<typeof ShadcnDrawerContent>,
    DrawerContentVariantProps {}

export interface DrawerHeaderProps
  extends React.ComponentPropsWithoutRef<'div'> {}

export interface DrawerFooterProps
  extends React.ComponentPropsWithoutRef<'div'> {}

export interface DrawerTitleProps
  extends React.ComponentPropsWithoutRef<typeof ShadcnDrawerTitle> {}

export interface DrawerDescriptionProps
  extends React.ComponentPropsWithoutRef<typeof ShadcnDrawerDescription> {}

/**
 * Drawer component following HEN Design System standards.
 *
 * A thin wrapper around shadcn/ui drawer (built on Vaul) with HEN-specific design tokens.
 * All styling uses tokens from tailwind.config.ts.
 *
 * @example
 * // With size variant
 * <Drawer>
 *   <DrawerTrigger>Open Half Screen</DrawerTrigger>
 *   <DrawerContent size="1/2">
 *     <DrawerHeader>
 *       <DrawerTitle>Half Screen Drawer</DrawerTitle>
 *     </DrawerHeader>
 *   </DrawerContent>
 * </Drawer>
 */
export const Drawer = React.forwardRef<
  React.ElementRef<typeof ShadcnDrawer>,
  DrawerProps
>(({ ...props }, ref) => {
  return <ShadcnDrawer {...props} />;
});

Drawer.displayName = 'Drawer';

export const DrawerTrigger = React.forwardRef<
  React.ElementRef<typeof ShadcnDrawerTrigger>,
  DrawerTriggerProps
>(({ className, ...props }, ref) => {
  return <ShadcnDrawerTrigger className={cn(className)} ref={ref} {...props} />;
});

DrawerTrigger.displayName = 'DrawerTrigger';

export const DrawerPortal = React.forwardRef<
  React.ElementRef<typeof ShadcnDrawerPortal>,
  DrawerPortalProps
>(({ ...props }, ref) => {
  return <ShadcnDrawerPortal {...props} />;
});

DrawerPortal.displayName = 'DrawerPortal';

export const DrawerClose = React.forwardRef<
  React.ElementRef<typeof ShadcnDrawerClose>,
  DrawerCloseProps
>(({ className, ...props }, ref) => {
  return <ShadcnDrawerClose className={cn(className)} ref={ref} {...props} />;
});

DrawerClose.displayName = 'DrawerClose';

export const DrawerOverlay = React.forwardRef<
  React.ElementRef<typeof ShadcnDrawerOverlay>,
  DrawerOverlayProps
>(({ className, ...props }, ref) => {
  return <ShadcnDrawerOverlay className={cn(className)} ref={ref} {...props} />;
});

DrawerOverlay.displayName = 'DrawerOverlay';

export const DrawerContent = React.forwardRef<
  React.ElementRef<typeof ShadcnDrawerContent>,
  DrawerContentProps
>(({ className, size, ...props }, ref) => {
  return (
    <ShadcnDrawerContent
      className={cn(drawerContentVariants({ size }), className)}
      ref={ref}
      {...props}
    />
  );
});

DrawerContent.displayName = 'DrawerContent';

export const DrawerHeader = React.forwardRef<HTMLDivElement, DrawerHeaderProps>(
  ({ className, ...props }, ref) => {
    return (
      <ShadcnDrawerHeader className={cn(className)} ref={ref} {...props} />
    );
  },
);

DrawerHeader.displayName = 'DrawerHeader';

export const DrawerFooter = React.forwardRef<HTMLDivElement, DrawerFooterProps>(
  ({ className, ...props }, ref) => {
    return (
      <ShadcnDrawerFooter className={cn(className)} ref={ref} {...props} />
    );
  },
);

DrawerFooter.displayName = 'DrawerFooter';

export const DrawerTitle = React.forwardRef<
  React.ElementRef<typeof ShadcnDrawerTitle>,
  DrawerTitleProps
>(({ className, ...props }, ref) => {
  return <ShadcnDrawerTitle className={cn(className)} ref={ref} {...props} />;
});

DrawerTitle.displayName = 'DrawerTitle';

export const DrawerDescription = React.forwardRef<
  React.ElementRef<typeof ShadcnDrawerDescription>,
  DrawerDescriptionProps
>(({ className, ...props }, ref) => {
  return (
    <ShadcnDrawerDescription className={cn(className)} ref={ref} {...props} />
  );
});

DrawerDescription.displayName = 'DrawerDescription';
